import React from 'react';
import styled, { withTheme } from 'styled-components';
import type { Fund, InvestmentFactorForecast } from 'venn-api';
import { type Theme, Tooltip } from 'venn-ui-kit';
import { assertNotNil, getDisabledOptimizationMessage } from 'venn-utils';
import { OptimalPortfolioContext } from '../contexts';
import AllocationFooter, { type AllocationFooterProps } from './AllocationFooter';
import { AllocationHeader, type AllocationHeaderProps } from './AllocationHeader';
import type { AllocationTreeProps } from './AllocationTree';
import AllocationTree from './AllocationTree';
import { getColorsFromCompareType, hasPortfolioIncompleteTaxInfo } from './AllocationUtils';
import { Content, Grid } from './Layout';
import AllocationPlus from './AllocationPlus';

interface AllocationPanelContentProps
  extends Omit<
    AllocationHeaderProps & AllocationTreeProps & AllocationFooterProps & { theme: Theme },
    keyof AllocationPanelContentState | 'onMouseEnter' | 'onMouseLeave' | 'disabledOptimizationMessage'
  > {
  investmentForecasts?: { [key: string]: InvestmentFactorForecast };
  isStudio: boolean;
  shouldUseNetAssetsInsteadOfAllocation: boolean;
  showPlus: boolean;
  allocationPanelType: string;
  canUploadInvestments?: boolean;
  updateAllFunds?: (funds: Fund[], preventSubjectUpdate?: boolean) => void;
}

interface AllocationPanelContentState {
  isDraggingInProgress: boolean;
  comparisonColor: string;
  lightComparisonColor: string;
}

class AllocationPanelContent extends React.Component<AllocationPanelContentProps, AllocationPanelContentState> {
  state = {
    isDraggingInProgress: false,
    comparisonColor: '',
    lightComparisonColor: '',
  };

  scrollElement = React.createRef<HTMLDivElement>();

  intervalHandler?: number = undefined;

  static getDerivedStateFromProps(nextProps: AllocationPanelContentProps) {
    const { selected: compareType, theme } = nextProps;
    const [primaryColor, lighterColor] = getColorsFromCompareType(compareType, theme);

    return {
      comparisonColor: primaryColor,
      lightComparisonColor: lighterColor,
    };
  }

  componentWillUnmount() {
    clearInterval(this.intervalHandler);
  }

  onScrollUp = () => {
    clearInterval(this.intervalHandler);
    this.intervalHandler = window.setInterval(() => {
      if (this.scrollElement.current) {
        this.scrollElement.current.scrollBy({ top: -10 });
      }
    }, 60);
  };

  onScrollDown = () => {
    clearInterval(this.intervalHandler);
    this.intervalHandler = window.setInterval(() => {
      if (this.scrollElement.current) {
        this.scrollElement.current.scrollBy({ top: 10 });
      }
    }, 60);
  };

  onStopScroll = () => {
    clearInterval(this.intervalHandler);
  };

  setIsDragging = (isDragging: boolean) => {
    this.setState({ isDraggingInProgress: isDragging });
  };

  render() {
    const { portfolio, investmentForecasts } = this.props;
    const { isDraggingInProgress, comparisonColor, lightComparisonColor } = this.state;
    const { canRerunOptimization, showOptimizerConstraintsButton, hideComparisonColumn } = this.props;

    return (
      <StyledGrid
        data-html2canvas-ignore
        isDraggingInProgress={isDraggingInProgress}
        hideComparisonColumn={hideComparisonColumn}
      >
        <AllocationHeader
          hideMaster={this.props.hideMaster}
          selected={this.props.selected}
          onChangeComparison={this.props.onChangeComparison}
          masterName={this.props.masterName}
          lastSavedPortfolio={this.props.lastSavedPortfolio}
          hasAllocationError={this.props.hasAllocationError}
          setIsPercentageMode={this.props.setIsPercentageMode}
          isPercentageMode={this.props.isPercentageMode}
          invalidTotal={this.props.invalidTotal}
          isTradesView={this.props.isTradesView}
          toggleTradesView={this.props.toggleTradesView}
          tradesToggleDisabled={this.props.tradesToggleDisabled}
          hideComparisonColumn={this.props.hideComparisonColumn}
          comparisonColor={comparisonColor}
          lightComparisonColor={lightComparisonColor}
          onMouseEnter={isDraggingInProgress ? this.onScrollUp : undefined}
          onMouseLeave={isDraggingInProgress ? this.onStopScroll : undefined}
          disabledOptimizationMessage={getDisabledOptimizationMessage(portfolio, investmentForecasts)}
        />
        <ContentWrapper>
          <StyledContent ref={this.scrollElement} color={comparisonColor} hideComparisonColumn={hideComparisonColumn}>
            <AllocationTree
              onSelectStrategy={this.props.portfolio?.historical ? undefined : this.props.onSelectStrategy}
              onDragFinished={this.onStopScroll}
              setIsDragging={this.setIsDragging}
              portfolio={this.props.portfolio}
              selectedStrategyId={this.props.selectedStrategyId}
              allOriginalNodes={this.props.allOriginalNodes}
              allCompareNodes={this.props.allCompareNodes}
              allGhostChildren={this.props.allGhostChildren}
              hideCompareValue={this.props.hideCompareValue}
              compareLoading={this.props.compareLoading}
              onUpdatePortfolio={this.props.onUpdatePortfolio}
              isPercentageMode={this.props.isPercentageMode}
              baseAllocation={this.props.baseAllocation}
              isTradesView={this.props.isTradesView}
              orignalBaseAllocation={this.props.orignalBaseAllocation}
              allUpdatedFunds={this.props.allUpdatedFunds}
              updateFund={this.props.updateFund}
              hasAccessToCompare={this.props.hasAccessToCompare}
              hideComparisonColumn={this.props.hideComparisonColumn}
              history={this.props.history}
              shouldUseNetAssetsInsteadOfAllocation={this.props.shouldUseNetAssetsInsteadOfAllocation}
              shouldAccountForDisplayingPlus={this.props.showPlus}
            />
            {/* Comparison column overlay with tooltip */}
            {canRerunOptimization && showOptimizerConstraintsButton && (
              <TooltipWrapper>
                <StyledTooltip block hideArrow content="Rerun or Save to update optimization">
                  <TooltipTrigger />
                </StyledTooltip>
              </TooltipWrapper>
            )}
          </StyledContent>
          {/* Plus button fixed at bottom of content area, above the footer */}
          {this.props.showPlus && this.props.allocationPanelType === 'full' && (
            <AllocationPlus
              canSave={this.props.canSave}
              updatePortfolio={this.props.onUpdatePortfolio}
              portfolio={portfolio}
              strategyId={this.props.selectedStrategyId}
              updateAllFunds={this.props.updateAllFunds}
              canUploadInvestments={this.props.canUploadInvestments}
            />
          )}
        </ContentWrapper>
        {/* Footer stays at the bottom of the panel */}
        <AllocationFooter
          {...(this.props.isPercentageMode
            ? {
                isPercentageMode: true,
                baseAllocation: assertNotNil(this.props.baseAllocation),
              }
            : {
                isPercentageMode: false,
              })}
          comparisonColor={comparisonColor}
          showMouseEventsOverlay={isDraggingInProgress}
          onMouseEnter={isDraggingInProgress ? this.onScrollDown : undefined}
          onMouseLeave={isDraggingInProgress ? this.onStopScroll : undefined}
          canSave={this.props.canSave}
          canReset={this.props.canReset}
          onSave={this.props.onSave}
          onSavedAsNewPortfolio={this.props.onSavedAsNewPortfolio}
          getSaveAsSuccessCustomNotification={this.props.getSaveAsSuccessCustomNotification}
          onReset={this.props.onReset}
          portfolio={this.props.portfolio}
          selectedStrategyId={this.props.selectedStrategyId}
          hasAllocationError={this.props.hasAllocationError}
          showOptimizerConstraintsButton={this.props.showOptimizerConstraintsButton}
          onOptimizeSuccess={this.props.onOptimizeSuccess}
          optimizationError={this.props.optimizationError}
          canRerunOptimization={this.props.canRerunOptimization}
          onRerunOptimization={this.props.onRerunOptimization}
          customAllocationConstraints={this.props.customAllocationConstraints}
          hasComparison={this.props.hasComparison}
          onApplyComparisonAllocationsToCurrent={this.props.onApplyComparisonAllocationsToCurrent}
          hideComparisonColumn={this.props.hideComparisonColumn}
          isStudio={this.props.isStudio}
          hasIncompleteTaxInfo={hasPortfolioIncompleteTaxInfo(this.props.portfolio)}
        />
      </StyledGrid>
    );
  }
}

const AllocationPanelContentWithContext = (props: Omit<AllocationPanelContentProps, 'investmentForecasts'>) => (
  <OptimalPortfolioContext.Consumer>
    {({ investmentForecasts }) => <AllocationPanelContent {...props} investmentForecasts={investmentForecasts} />}
  </OptimalPortfolioContext.Consumer>
);

export default withTheme(AllocationPanelContentWithContext);

const StyledGrid = styled(Grid)<{ isDraggingInProgress: boolean }>`
  ${({ isDraggingInProgress }) =>
    isDraggingInProgress &&
    `
    * {
      cursor: move !important;
      user-select: none;
      -ms-user-select: none;
    }
  `}
`;

const TooltipWrapper = styled.div`
  position: absolute;
  width: 105px;
  height: calc(100% - 62px);
  right: -1px;
  top: 62px;
`;

const StyledTooltip = styled(Tooltip)`
  > div:last-child {
    top: 33%;
    text-align: center;
  }
`;

const TooltipTrigger = styled.div`
  height: 100%;
  width: 100%;
`;

const StyledContent = styled(Content)`
  height: 100%;
`;

const ContentWrapper = styled.div`
  position: relative;
  grid-area: content;
  overflow: hidden;
`;
